import { ref, Ref, watch } from 'vue';
import { IFcisWidgetParameters } from 'fcis-widget/dist/types/interfaces/IInitializationConfig';
import { CONTEXT_TYPES } from '@dh-io-cscs/user-context-manager/dist/common/constants';
import { ProductPriceResponseDto } from '@dh-io-pls/pls-store';
import { CUSTOMER_TYPE, ICustomer, IVehicle } from 'fcis-widget/dist/types/interfaces/ICalculationModel';
import {
  findPriceBreakDownItem,
  getFcisCustomerType,
  getFcisEnvironment,
  getPlsPriceData,
  getRequestMode,
} from '../utils/finance.utils';

import { AUTHENTICATION_CONNECTION, AuthenticationState } from '@b2x/authentication-library';
import { B2X_ACTIVE_PROFILE_CONNECTION_NAME, B2XActiveProfileState } from '@dh-io-mhb2b/active-profile';
import { initializeStore } from '@seamless/store';
import { EmhConfig, Product } from '@dh-io-emh/search-result-store';

const store = initializeStore();
const activeCustomerGroupId = ref<string>();

const jweToken = ref<string | null>(null);
const activeProfileCustomerType = ref<CONTEXT_TYPES>();

store.subscribe(B2X_ACTIVE_PROFILE_CONNECTION_NAME, (newActiveProfile: B2XActiveProfileState) => {
  activeCustomerGroupId.value = newActiveProfile?.activeCustomerGroup?.domainRef?.id;
  activeProfileCustomerType.value = newActiveProfile?.activeCustomerGroup?.customerType?.businessType as CONTEXT_TYPES;
});

store.subscribe(AUTHENTICATION_CONNECTION, (newAuthState: AuthenticationState) => {
  jweToken.value = newAuthState.jwe;
});

const getNetExShowroomPrice = (plsData: ProductPriceResponseDto | void) => {
  if (!plsData) {
    throw new Error('getNetExShowroomPrice()::plsData is undefined');
  }

  const priceBreakDown = findPriceBreakDownItem(plsData as ProductPriceResponseDto, 'NET_EX_SHOWROOM');

  if (!priceBreakDown?.amount) {
    throw new Error('getNetExShowroomPrice()::NET_EX_SHOWROOM does not exist');
  }

  return parseFloat(priceBreakDown.amount);
};

export const useFcisWidget = ({
  fcisWidgetEl,
  product,
  customerType,
  emhConfig,
  postCode,
  locale,
}: {
  fcisWidgetEl: Ref<HTMLFcisWidgetElement | HTMLFcisSummaryWidgetElement | undefined>;
  product: Ref<Product | undefined>;
  customerType: Ref<CONTEXT_TYPES | undefined>;
  emhConfig: EmhConfig;
  postCode: Ref<string | null>;
  locale: Ref<string>;
}) => {
  const { shopCountry, fincalc, tracking, apiMode, b2xSwitch, lang } = emhConfig;
  const caller = fincalc.callerSummary;
  const fcisEnv = getFcisEnvironment(apiMode);

  const setFcisWidgetParameters = async (): Promise<void> => {
    if (!fcisWidgetEl.value || !product.value) return;

    // customerType can be optionally provided separately from B2X toggle,
    // e.g. via PCP option, which takes precedence over B2X user context
    const requestMode = getRequestMode(product.value);

    const vehicle: IVehicle = {
      condition: { condition: 'new' },
    };

    const customer: ICustomer = {};

    const fcisParams: IFcisWidgetParameters = {
      market: shopCountry,
      caller,
      locale: locale.value,
      vehicle,
      customer,
    };

    if (b2xSwitch.enable && customerType.value) {
      // Note: adding customer param will also hide the customer type dropdown from the widget
      customer.type = getFcisCustomerType(customerType.value) as CUSTOMER_TYPE;
    }

    let plsData: ProductPriceResponseDto | void;

    if (fincalc.waitPLC) {
      plsData = await getPlsPriceData({
        product: product.value,
        shopCountry,
        lang,
        apiMode,
        customerType: customerType.value,
        postCode: postCode.value,
        activeCustomerGroupId: activeCustomerGroupId.value,
        jweToken: jweToken.value,
      });
    }

    switch (requestMode) {
      case 'overseas': {
        vehicle.technicalIdReference = product.value.vehicleId;

        let rawValue: number;

        if (fincalc.widgetParameters.usePlsNetExShowroomPrice) {
          rawValue = getNetExShowroomPrice(plsData);
        } else {
          rawValue = plsData?.purchasePrice.amount
            ? parseFloat(plsData.purchasePrice.amount)
            : product.value.price.value;
        }

        vehicle.prices = [
          {
            id: 'purchasePrice',
            currency: product.value.price.currencyIso,
            rawValue,
            // any cast because 'charges' is optional but typed as mandatory
            charges: undefined as any,
          },
        ];

        break;
      }
      default:
        fcisParams.vehicleId = product.value.commissionNumber;

        // Use additional values, prices and vehicle configuration from PLC if available and configured to do so
        if (fincalc.widgetParameters.useAdditionalParams && plsData) {
          fcisParams.vehicle = { ...vehicle, ...((plsData.financeLeasingInfo as any)?.vehicle as IVehicle) };
          fcisParams.customer = { ...customer, ...(plsData.financeLeasingInfo as any)?.customer };
        }

        break;
    }

    fcisWidgetEl.value.parameters = fcisParams;
  };

  watch([fcisWidgetEl, product, customerType, postCode, activeCustomerGroupId], setFcisWidgetParameters, {
    immediate: true,
  });

  return { caller, environment: fcisEnv, parentApplicationId: tracking.id };
};
